import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { TradePlanData } from '../../../../classes/tradePlan/tradePlanData';
import { TradePlanFormProvider } from '../../../../classes/tradePlan/tradePlanProvider';
import { useWebsocketStore } from '../../../../stores/websocketStore/websocketProvider';
import { BorderAndLabel } from '../../../atomic/atomicComponentLibrary/utility/borderAndLabel/BorderAndLabel';
import { fontSizes } from '../../../atomic/text/Text';
import { BottomRow } from './components/bottomRow/BottomRow';
import { TradingChart } from './components/chart/TradingChart';
import { ExitTacticSection } from './components/exitTactics/ExitTacticSection';
import { OptionChainsContainer } from './components/options/optionChains/OptionChainsContainer';
import { Options } from './components/options/Options';
import { QuickTrade } from './components/quickTrade/QuickTrade';
import { TopRow } from './components/topRow/TopRow';
import style from './TradingModule.module.css';
type StoreRef = {
  current: TradePlanData | null;
};
export const defaultTemplateConstant = '__defaultTradingModuleTemplate12345!';
export const TradingModule = observer(() => {
  const [loadingStore, setLoadingStore] = useState(true);
  const websocketStore = useWebsocketStore();
  const storeRef: StoreRef = useRef(null);

  useEffect(() => {
    if (websocketStore) {
      storeRef.current = new TradePlanData(websocketStore);
      setLoadingStore(false);
    }
  }, [websocketStore]);

  const { savedTradePlanTemplatesStore } = useWebsocketStore();

  const templatesList =
    Object.keys(savedTradePlanTemplatesStore.savedTemplates) || [];

  const [numberOfCandles, setNumberOfCandles] = useState(30);
  const [timeUnit, setTimeUnit] = useState('MINUTES');
  const [candleDuration, setCandleDuration] = useState(1);
  const [showCandleNumbers, setShowCandleNumbers] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [autoSend, setAutoSend] = useState(false);
  const [allowTemplates, setAllowTemplates] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState(false);
  const [loadedDefaultTemplate, setLoadedDefaultTemplate] = useState(false);
  useEffect(() => {
    if (!loadedDefaultTemplate) {
      if (templatesList.includes(defaultTemplateConstant)) {
        const json =
          savedTradePlanTemplatesStore.savedTemplates[defaultTemplateConstant]
            .tradePlanJson;
        storeRef.current.setFormToTemplate(json);
        setLoadedDefaultTemplate(true);
      }
    }
  }, [templatesList]);

  useEffect(() => {
    if (storeRef.current && allowTemplates) {
      if (selectedTemplate === 'none' && loadedTemplate) {
        storeRef.current.resetToDefaults();
        setLoadedTemplate(false);
      } else {
        const template =
          savedTradePlanTemplatesStore.savedTemplates[selectedTemplate]
            ?.tradePlanJson ?? null;
        if (template) {
          storeRef.current.setFormToTemplate(template);
        }
        setLoadedTemplate(true);
      }
    }
  }, [selectedTemplate]);

  if (storeRef.current === null || loadingStore) return null;

  return (
    <TradePlanFormProvider store={storeRef.current}>
      <div className={style.outerContainer}>
        <div className={style.body}>
          <TopRow
            templatesList={templatesList}
            setSelectedTemplate={setSelectedTemplate}
            selectedTemplate={selectedTemplate}
            allowTemplates={allowTemplates}
            setAllowTemplates={setAllowTemplates}
            timeUnit={timeUnit}
            setTimeUnit={setTimeUnit}
            candleDuration={candleDuration}
            setCandleDuration={setCandleDuration}
            showCandleNumbers={showCandleNumbers}
            setShowCandleNumbers={setShowCandleNumbers}
            numberOfCandles={numberOfCandles}
            setNumberOfCandles={setNumberOfCandles}
          />
          <div className={style.mainSection}>
            <BorderAndLabel
              backgroundColor="black"
              margin="10px 0 0 0"
              label="Chart"
              color="white"
              fontSize={fontSizes.body3}
            >
              <TradingChart
                autoSend={autoSend}
                numberOfCandles={numberOfCandles}
                timeUnit={timeUnit}
                candleDuration={candleDuration}
                id={'1'}
                showCandleNumbers={showCandleNumbers}
              />
            </BorderAndLabel>
            <div>
              <QuickTrade margin="10px 0 0 10px" />
              {storeRef.current.instrument === 'option' ? (
                <Options margin="10px 0 0 10px" />
              ) : (
                <div></div>
              )}
            </div>
            {storeRef.current.instrument === 'option' ? (
              <OptionChainsContainer />
            ) : (
              <ExitTacticSection
                candleDuration={candleDuration}
                timeUnit={timeUnit}
              />
            )}
            {storeRef.current.instrument === 'option' ? (
              <ExitTacticSection
                candleDuration={candleDuration}
                timeUnit={timeUnit}
              />
            ) : null}
          </div>
          <BottomRow autoSend={autoSend} setAutoSend={setAutoSend} />
        </div>
      </div>
    </TradePlanFormProvider>
  );
});
